// Bitácora compartida: la línea de tiempo de eventos auditados (`.utl`) que
// estrenó la ficha de unidad, generalizada para el feed de la empresa del
// Tablero (listAudit). Aquí viven tres piezas:
//   · auditActionLabel — la acción ('unit.status', 'trip.assigned'…) como se
//     lee en español, con sufijos dinámicos ('trip.status.done' → «Viaje
//     terminado»).
//   · auditNote — el detalle de la nota: traduce claves del contrato (tipos
//     de servicio, severidades, posiciones de llanta, kind de documento) y
//     descarta las que traen ids internos que no aportan al leer.
//   · AuditRow — la fila con punto + etiqueta + quién y cuándo. `subject` es
//     el nombre del sujeto del evento: en la bitácora de unidad va vacío (la
//     ficha ya dice la unidad); en el feed de empresa lo resuelve
//     auditSubject contra los mapas del snapshot (unidad, miembro, viaje,
//     documento, reporte, llanta o lugar). `onClick` la vuelve interactiva:
//     solo la usa el feed del Tablero para navegar a la ficha del sujeto.

import type { CSSProperties } from 'react';
import { COMPANY_STATUS, REPORT_SEVERITY, ROLE_LABEL, SERVICE_KIND, TIRE_LOCATION, TIRE_REASON, TRIP_STATUS, UNIT_STATUS, dateTime, timeAgo, unitCategoryOf, unitName } from '../format';
import { INSPECTION_KIND_LABEL } from '../inspection';
import { tirePositionLabel } from '../tirePositions';
import { Icon } from '../components/ui';
import { DOC_CATALOG, docLabel } from './Documents';
import type { AuditEvent, CompanyInfo, CompanyStatus, Session, TireLocation, TireRemoveReason, Trip, TripDocKind, TripStatus, Unit, UnitDocument, UnitReport, UnitReportSeverity, UnitServiceKind, UnitStatus, UnitTire } from '../types';
import { TRIP_DOC_KINDS } from '../types';

/** Acciones de la bitácora → cómo se leen en la línea de tiempo. Cubre los
    eventos de unidad (ficha) más los del feed de empresa del Tablero. */
export const AUDIT_LABEL: Record<string, string> = {
  'unit.saved': 'Ficha guardada',
  'unit.status': 'Cambio de estado',
  'meter.reading': 'Lectura del contador',
  'unit.photoAdded': 'Foto agregada',
  'unit.photoRemoved': 'Foto quitada',
  'unit.service': 'Servicio registrado',
  'unit.serviceUpdated': 'Servicio actualizado',
  'unit.serviceDeleted': 'Servicio eliminado',
  'unit.inspection': 'Inspección registrada',
  'unit.inspectionDeleted': 'Inspección eliminada',
  'unitReport.created': 'Reporte de falla',
  'unitReport.inShop': 'Reporte pasó a taller',
  'unitReport.resolved': 'Reporte resuelto',
  'unitReport.open': 'Reporte reabierto',
  'trip.assigned': 'Viaje asignado',
  'trip.updated': 'Viaje actualizado',
  'trip.defectAuthRequest': 'El chofer pidió autorización de salida',
  'trip.defectOverride': 'Salida con defectos autorizada',
  'doc.saved': 'Documento guardado',
  'doc.reviewed': 'Documento revisado',
  'doc.renewed': 'Documento renovado',
  'doc.deleted': 'Documento eliminado',
  'tripDoc.added': 'Documento adjuntado al viaje',
  'tripDoc.deleted': 'Documento del viaje eliminado',
  'member.unitsSet': 'Operador habitual',
  'unit.tireMounted': 'Llanta montada',
  'unit.tireRemoved': 'Llanta desmontada',
  'unit.tireUpdated': 'Llanta editada',
  'unit.tireRotated': 'Llantas rotadas',
  'unit.tireLocation': 'Llanta movida',
  'company.registered': 'Empresa registrada',
  'company.codeRotated': 'Código de invitación nuevo',
  'company.joined': 'Miembro se unió',
  'company.left': 'Miembro salió de la empresa',
  'company.memberRemoved': 'Miembro quitado de la empresa',
  'company.roleSet': 'Cambio de rol',
  'company.favorite.set': 'Lugar frecuente actualizado',
  'company.noteCreated': 'Nota de lugar agregada',
  'company.noteUpdated': 'Nota de lugar actualizada',
  'company.noteDeleted': 'Nota de lugar eliminada',
  'company.placeCreated': 'Lugar privado creado',
  'company.placeUpdated': 'Lugar privado actualizado',
  'company.placeDeleted': 'Lugar privado eliminado',
  'membership.joined': 'Registro en el directorio',
  'membership.rejected': 'Acceso rechazado',
  'session.newDevice': 'Sesión en dispositivo nuevo',
  'session.revoked': 'Sesión cerrada',
  'account.emailVerified': 'Correo verificado',
  'account.passwordReset': 'Contraseña restablecida',
  'place.created': 'Lugar creado',
  'place.updated': 'Lugar actualizado',
  'place.deleted': 'Lugar eliminado',
  'place.photoAdded': 'Foto del lugar agregada',
  'place.photoRemoved': 'Foto del lugar quitada',
  'visit.started': 'Visita iniciada',
  'visit.cancelled': 'Visita cancelada',
  'visit.autoClosed': 'Visita cerrada sola',
  'visit.photoAdded': 'Foto de visita agregada',
  'visit.photoRemoved': 'Foto de visita quitada',
  'contribution.approved': 'Aportación aprobada',
  'contribution.rejected': 'Aportación rechazada',
};

/** Etiqueta visible de la acción: directa del mapa, con sufijos dinámicos
    ('trip.status.done' → «Viaje terminado», 'company.status.active' →
    «Empresa autorizada»); la acción cruda queda de último recurso. */
export function auditActionLabel(action: string): string {
  if (AUDIT_LABEL[action]) return AUDIT_LABEL[action];
  if (action.startsWith('trip.status.')) {
    const st = TRIP_STATUS[action.slice('trip.status.'.length) as TripStatus];
    return st ? `Viaje ${st.label.toLowerCase()}` : 'Viaje actualizado';
  }
  if (action.startsWith('company.status.')) {
    const st = COMPANY_STATUS[action.slice('company.status.'.length) as CompanyStatus];
    return st ? `Empresa ${st.label.toLowerCase()}` : 'Cambio de estado de la empresa';
  }
  return action;
}

/** Detalle legible de la nota del evento: traduce claves (estado, tipo de
    servicio, severidad, posición de llanta) y deja el texto libre tal cual.
    `unit` es la unidad del evento (bitácora de unidad: la ficha misma; feed
    de empresa: la resuelta por auditSubject) para traducir posiciones de
    llanta con su catálogo. `resolve` convierte uids de la nota en nombres —
    solo lo usa el feed para el driverId que traen los eventos de viaje. */
export function auditNote(entry: AuditEvent, opts: {
  unit?: Unit;
  company?: CompanyInfo | null;
  resolve?(uid: string): string | undefined;
} = {}): string {
  const { unit, company, resolve } = opts;
  const note = entry.note ?? '';
  if (entry.action === 'unit.tireRotated') {
    // Nota «<pos-key>→<pos-key>»: ambas posiciones se traducen con el
    // catálogo de la unidad (tolera también «->» por si llega ASCII).
    const parts = note.split(/\s*(?:→|->)\s*/).filter(Boolean);
    const label = (k: string) => unit ? tirePositionLabel(unit.kind, unitCategoryOf(unit, company), k) : k;
    return parts.length === 2 ? `${label(parts[0])} → ${label(parts[1])}` : note;
  }
  if (entry.action === 'unit.tireLocation') {
    // Nota «<location>» o «<pos-key> — <location>»: la ubicación se traduce
    // y la posición también cuando viene.
    return note.split(' — ').map(part => {
      const p = part.trim();
      if (p in TIRE_LOCATION) return TIRE_LOCATION[p as TireLocation].label;
      return unit ? tirePositionLabel(unit.kind, unitCategoryOf(unit, company), p) : p;
    }).join(' — ');
  }
  if (entry.action === 'unit.tireMounted' || entry.action === 'unit.tireRemoved' || entry.action === 'unit.tireUpdated') {
    // Nota «<pos-key>» o «<pos-key> — <detalle>»: la posición se traduce con
    // el catálogo de la unidad; el detalle (motivo de desmontaje) también.
    const sep = note.indexOf(' — ');
    const key = sep < 0 ? note : note.slice(0, sep);
    const label = unit ? tirePositionLabel(unit.kind, unitCategoryOf(unit, company), key) : key;
    const rest = sep < 0 ? '' : note.slice(sep + 3);
    const restText = entry.action === 'unit.tireRemoved' ? (TIRE_REASON[rest as TireRemoveReason] ?? rest) : rest;
    return [label, restText].filter(Boolean).join(' — ');
  }
  if (entry.action === 'unit.status') {
    const sep = note.indexOf(' — ');
    const status = (sep < 0 ? note : note.slice(0, sep)) as UnitStatus;
    const reason = sep < 0 ? '' : note.slice(sep + 3);
    const label = UNIT_STATUS[status]?.label ?? note;
    return reason ? `${label} — ${reason}` : label;
  }
  if (entry.action === 'unit.service' || entry.action === 'unit.serviceDeleted' || entry.action === 'unit.serviceUpdated')
    return SERVICE_KIND[note as UnitServiceKind] ?? note;
  // Nota «<kind>:<n>» — «pre-viaje» solo, o «pre-viaje · 3 defectos».
  if (entry.action === 'unit.inspection' || entry.action === 'unit.inspectionDeleted') {
    const [kind, count] = note.split(':');
    const label = (INSPECTION_KIND_LABEL[kind] ?? kind).toLowerCase();
    const n = Number(count);
    return Number.isFinite(n) && n > 0 ? `${label} · ${n} defecto${n === 1 ? '' : 's'}` : label;
  }
  if (entry.action === 'unitReport.created') return REPORT_SEVERITY[note as UnitReportSeverity]?.label ?? note;
  if (entry.action === 'meter.reading') {
    // Una lectura malformada no pinta «NaN» — se omite la nota.
    const n = Number(note);
    return Number.isFinite(n) ? n.toLocaleString('es-MX') : '';
  }
  // La nota de los documentos del viaje trae el tipo (cartaPorte, pod…).
  if (entry.action === 'tripDoc.added' || entry.action === 'tripDoc.deleted') return TRIP_DOC_KINDS[note as TripDocKind] ?? note;
  // Los eventos de viaje traen el driverId en la nota — un uid nunca se
  // muestra crudo: se resuelve a nombre y, sin resolver, se omite (el
  // sujeto ya dice el chofer y la ruta).
  if (entry.action === 'trip.assigned' || entry.action === 'trip.updated' || entry.action.startsWith('trip.status.'))
    return resolve?.(note) ?? '';
  // Documentos: la nota trae el `type` (o el id de la versión anterior al
  // renovar); el review trae la decisión del servidor.
  if (entry.action === 'doc.saved' || entry.action === 'doc.deleted')
    return DOC_CATALOG.find(t => t.type === note)?.label ?? note;
  if (entry.action === 'doc.renewed') return '';
  if (entry.action === 'doc.reviewed') {
    if (note === 'verified') return 'Verificado';
    if (note.startsWith('rejected')) {
      const reason = note.slice('rejected'.length).replace(/^:\s*/, '');
      return reason ? `Rechazado — ${reason}` : 'Rechazado';
    }
    return note;
  }
  if (entry.action === 'company.roleSet') return ROLE_LABEL[note] ?? note;
  // Las notas de empresa traen ids internos (noteId, companyId) o el nombre
  // de la propia empresa — el sujeto/actor de la fila ya lo dicen.
  if (entry.action.startsWith('company.')) return '';
  return note;
}

/** Evento de la bitácora: punto + etiqueta (con sujeto en el feed de
    empresa) + nota + quién y cuándo. `memberName` es el actor con su
    respaldo («la oficina»); `resolveName` traduce uids dentro de la nota. */
export function AuditRow({ entry, subject, unit, company, memberName, resolveName, onClick }: {
  entry: AuditEvent;
  /** Nombre del sujeto (feed de empresa); vacío en la bitácora de unidad. */
  subject?: string;
  unit?: Unit;
  company?: CompanyInfo | null;
  memberName(uid: string): string;
  resolveName?(uid: string): string | undefined;
  /** Con onClick la fila se vuelve clickable (deep-link del feed del Tablero):
      un <button> dentro del .utl-item — reseteado en línea porque no hay
      reglas .utl-item para button — más el chevron de afordance de .arow.
      Sin él la fila queda como siempre (bitácora de unidad, que no navega). */
  onClick?(): void;
}) {
  const label = auditActionLabel(entry.action);
  const note = auditNote(entry, { unit, company, resolve: resolveName });
  // Si la nota ya va dentro del sujeto («Seguro (México)» dentro de
  // «Seguro (México) — Tracto R-01», o el propio «María») no se repite.
  const detail = [
    note && !(subject && subject.includes(note)) ? note : '',
    memberName(entry.actorId),
    dateTime(entry.createdAt),
  ].filter(Boolean).join(' · ');
  const inner = <>
    <span className="utl-dot" aria-hidden="true" />
    <div className="grow">
      <div className="row between multiline">
        <span className="small name">{label}{subject ? ` — ${subject}` : ''}</span>
        <span className="tiny muted">{timeAgo(entry.createdAt)}</span>
      </div>
      <div className="tiny muted">{detail}</div>
    </div>
  </>;
  if (!onClick) return <div className="utl-item">{inner}</div>;
  // El padding vertical de .utl-item pasa al <button> para que TODA la fila
  // responda al toque; el .utl-item externo (padding 0) conserva el
  // separador border-top de la línea de tiempo, que un button con clase
  // rompería (los estilos inline ganan a la regla `.utl-item + .utl-item`).
  return <div className="utl-item" style={{ padding: 0 }}>
    <button type="button" onClick={onClick} style={UTL_ROW_BTN}>
      {inner}
      <span style={UTL_ROW_CHEV}><Icon name="chevronRight" size={15} /></span>
    </button>
  </div>;
}

// Reset inline del <button> interactivo de AuditRow: replica el aire de
// .utl-item (gap 11, padding 8/0) sin fondo ni borde del UA.
const UTL_ROW_BTN: CSSProperties = {
  display: 'flex', alignItems: 'flex-start', gap: 11, width: '100%', minWidth: 0,
  padding: '8px 0', border: 0, background: 'none',
  font: 'inherit', color: 'inherit', textAlign: 'left', cursor: 'pointer',
};
const UTL_ROW_CHEV: CSSProperties = { alignSelf: 'center', flex: 'none', display: 'flex', color: 'var(--muted)' };

/** Mapas de nombres del snapshot para auditSubject — la misma data que la
    vista ya tiene a la mano; todo opcional menos unidades y miembros. */
export interface AuditSubjects {
  units: ReadonlyMap<string, Unit>;
  members: ReadonlyMap<string, Session>;
  trips?: ReadonlyMap<string, Trip>;
  docs?: ReadonlyMap<string, UnitDocument>;
  reports?: ReadonlyMap<string, UnitReport>;
  tires?: ReadonlyMap<string, UnitTire>;
  company?: CompanyInfo | null;
  placeName?(id: string): string | undefined;
}

/** Sujeto del evento para el feed de empresa: resuelve el nombre a mostrar
    junto a la etiqueta («Tracto R-01», «Seguro (México) — Tracto R-01»,
    «María · Central → Norte»…) y la unidad relacionada cuando aplica, para
    que auditNote traduzca posiciones de llanta con su catálogo. */
export function auditSubject(entry: AuditEvent, s: AuditSubjects): { text: string; unit?: Unit } {
  const a = entry.action;
  const memberText = (uid: string) => {
    const m = s.members.get(uid);
    return m ? (m.displayName || m.email) : '';
  };
  const unitText = (u: Unit | undefined, fallback = 'Unidad') => u ? unitName(u, s.company) : fallback;

  // Llanta: el sujeto del evento es la llanta — su unidad sale por el stock
  // del snapshot (solo activas; las desmontadas viejas caen a «Llanta»).
  if (a === 'unit.tireMounted' || a === 'unit.tireRemoved' || a === 'unit.tireUpdated' || a === 'unit.tireLocation') {
    const tire = s.tires?.get(entry.subjectId);
    const unit = tire ? s.units.get(tire.unitId) : undefined;
    return { text: unit ? unitName(unit, s.company) : 'Llanta', unit };
  }
  // Unidad como sujeto directo (ficha, estado, contador, servicios,
  // inspecciones, fotos, rotación — tireRotated lleva unitId, no tireId).
  if (a.startsWith('unit.') || a === 'meter.reading') {
    const unit = s.units.get(entry.subjectId);
    return { text: unitText(unit), unit };
  }
  // Reporte de falla: el sujeto es el reporte; la unidad cuelga de él.
  if (a.startsWith('unitReport.')) {
    const unitId = s.reports?.get(entry.subjectId)?.unitId;
    const unit = unitId ? s.units.get(unitId) : undefined;
    return { text: unit ? unitName(unit, s.company) : 'Reporte de falla', unit };
  }
  // Documento con vencimiento: nombre + a quién pertenece (unidad, operador
  // o empresa). Los borrados ya no están en el snapshot: solo «Documento».
  if (a.startsWith('doc.')) {
    const doc = s.docs?.get(entry.subjectId);
    if (!doc) return { text: 'Documento' };
    const owner = doc.subjectType === 'unit'
      ? unitText(s.units.get(doc.subjectId))
      : doc.subjectType === 'member'
        ? memberText(doc.subjectId) || 'Operador'
        : s.company?.name || 'Empresa';
    return { text: `${docLabel(doc)} — ${owner}` };
  }
  if (a.startsWith('tripDoc.')) return { text: 'Documento del viaje' };
  if (a.startsWith('trip.')) {
    const t = s.trips?.get(entry.subjectId);
    if (!t) return { text: 'Viaje' };
    const route = t.stops.map(x => s.placeName?.(x.placeId)).filter(Boolean).join(' → ');
    return { text: [t.driverName, route].filter(Boolean).join(' · ') || 'Viaje' };
  }
  // Miembros como sujeto: unidad habitual, baja de la empresa, cambio de rol.
  if (a === 'member.unitsSet' || a === 'company.memberRemoved' || a === 'company.roleSet')
    return { text: memberText(entry.subjectId) || entry.note || 'Miembro' };
  // Eventos con placeId (lugares privados, frecuentes, notas, visitas…).
  if (entry.placeId) {
    const name = s.placeName?.(entry.placeId);
    return { text: name || 'Lugar' };
  }
  if (a.startsWith('company.')) return { text: s.company?.name || 'la empresa' };
  // Eventos de cuenta/membresía (directorio): el sujeto es la persona.
  const who = memberText(entry.subjectId);
  return { text: who };
}
